import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, ArrowRight, Zap, Star, Crown, HelpCircle } from 'lucide-react';

export default function Prices() {
  const navigate = useNavigate();


  const plans = [
    {
      name: "Growth",
      icon: <Zap className="w-6 h-6" />,
      description: "Establish a profitable baseline and prove the data model.",

      adSpend: "Efficiency Focus",
      popular: false,
      color: "gray",
      features: [
        "Management of 2 Core Channels",
        "Basic Conversion Tracking Setup",
        "Monthly Performance Audits",
        "Strategy Pivots",
        "Focus on \"Clean Data\" & CPA Clarity",
      ],
      excluded: [
        "Multi-Channel Management",
        "Creative Testing Lab",
        "Predictive Modelling",
        "24/7 Slack Support",
      ],
    },
    {
      name: "Velocity",
      icon: <Star className="w-6 h-6" />,
      description: "Rapidly increase market share while maintaining a healthy ROAS.",

      adSpend: "Scaling Focus",
      popular: true,
      color: "teal",
      features: [
        "Management of Multi-Channels",
        "Precision Retargeting Strategies",
        "Creative Testing Lab (Monthly Refreshes)",
        "Bi-weekly Strategy Calls",
        "Live Data Dashboard",
        "Advanced Analytics & Lookalikes",
      ],
      excluded: [
        "Full-Funnel Strategy",
        "Predictive Modelling",
        "Strategic Business Consulting",
      ],
    },
    {
      name: "Surge",
      icon: <Crown className="w-6 h-6" />,
      description: "Total category take-over and long-term brand equity.",

      adSpend: "Dominance Focus",
      popular: false,
      color: "dark",
      features: [
        "Omni-channel Management",
        "Full-Funnel Strategy",
        "Predictive Modelling (6-month forecast)",
        "Weekly High-level Consulting",
        "24/7 Slack Support",
        "Strategic Business Consulting",
        "Handling Massive Lead Influx",
      ],
      excluded: [],
    },
  ];

  const fadeUp = (delay = 0) => ({
    initial: { opacity: 0, y: 25 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, delay, ease: "easeOut" },
  });

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">

      {/* ─── Hero Section ─── */}
      <div className="relative bg-gradient-to-br from-[#00809D] via-[#006d87] to-[#005568] overflow-hidden">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-white/5 rounded-full translate-y-1/2 -translate-x-1/3" />
        <div className="absolute top-1/2 right-1/3 w-20 h-20 bg-white/5 rounded-xl rotate-45" />
        <div className="absolute bottom-1/3 left-1/4 w-16 h-16 bg-white/5 rounded-full" />

        <div className="relative z-10 pt-16 sm:pt-20 lg:pt-24 pb-20 sm:pb-24 lg:pb-32 px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp()}>
            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-md px-5 py-2 rounded-full mb-6 border border-white/20">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
              </span>
              <span className="text-white/90 font-medium text-sm tracking-wide">TRANSPARENT PRICING</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-white mb-5 leading-tight tracking-tight">
              Choose Your{" "}
              <span className="block sm:inline">Growth Plan</span>
            </h1>
            <p className="text-lg sm:text-xl text-white/80 max-w-2xl mx-auto leading-relaxed mb-8">
              Tailored plans to match your growth stage.{" "}
              <span className="text-white font-semibold">No long-term contracts.</span>
            </p>
          </motion.div>
        </div>

        {/* Curved bottom */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 80V40C240 0 480 0 720 20C960 40 1200 60 1440 40V80H0Z" fill="white" />
          </svg>
        </div>
      </div>

      {/* ─── Pricing Cards ─── */}
      <div className="relative z-10 px-4 sm:px-6 lg:px-8 -mt-8 sm:-mt-10 pb-16 sm:pb-20 lg:pb-24">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 items-start">
            {plans.map((plan, i) => (
              <motion.div
                key={plan.name}
                {...fadeUp(0.1 + i * 0.15)}
                className={`relative group bg-white rounded-3xl border transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl overflow-hidden flex flex-col ${plan.popular
                  ? 'border-[#00809D] shadow-xl shadow-[#00809D]/10 ring-1 ring-[#00809D]/20 lg:scale-105 z-10'
                  : 'border-gray-200 shadow-lg hover:border-[#00809D]/30 hover:shadow-[#00809D]/10'
                  }`}
              >
                {/* Popular Badge */}
                {plan.popular && (
                  <div className="bg-gradient-to-r from-[#00809D] to-[#00a0c4] text-white text-center py-2.5 text-sm font-bold tracking-wider">
                    🔥 MOST POPULAR
                  </div>
                )}

                <div className="p-6 sm:p-8 flex flex-col flex-1">
                  {/* Plan Header */}
                  <div className="flex items-start gap-4 mb-5">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${plan.popular
                      ? 'bg-[#00809D] text-white'
                      : 'bg-[#00809D]/10 text-[#00809D]'
                      }`}>
                      {plan.icon}
                    </div>
                    <div>
                      <h3 className="text-xl sm:text-2xl font-bold text-gray-900">{plan.name}</h3>
                      <p className="text-sm text-gray-500 mt-0.5">{plan.description}</p>
                    </div>
                  </div>

                  {/* Focus Badge */}
                  <div className="mb-6 pb-6 border-b border-gray-100">
                    <span className="text-xs bg-[#00809D]/10 text-[#00809D] font-semibold px-3 py-1 rounded-full">
                      {plan.adSpend}
                    </span>
                  </div>

                  {/* Features */}
                  <div className="flex-1 mb-6">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
                      What's included
                    </p>
                    <ul className="space-y-3">
                      {plan.features.map((feature, j) => (
                        <li key={j} className="flex items-start gap-3">
                          <div className="w-5 h-5 rounded-full bg-[#00809D]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Check className="w-3 h-3 text-[#00809D]" />
                          </div>
                          <span className="text-sm text-gray-700 leading-relaxed">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {plan.excluded.length > 0 && (
                      <>
                        <div className="h-px bg-gray-100 my-4" />
                        <ul className="space-y-3">
                          {plan.excluded.map((feature, j) => (
                            <li key={j} className="flex items-start gap-3 opacity-40">
                              <div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <span className="w-2 h-0.5 bg-gray-400 rounded-full" />
                              </div>
                              <span className="text-sm text-gray-500 leading-relaxed line-through">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>

                  {/* CTA Button */}
                  <button
                    onClick={() => navigate('/contact')}
                    className={`group/btn w-full py-4 px-6 rounded-xl font-semibold text-sm transition-all duration-300 flex items-center justify-center gap-2 hover:scale-[1.02] active:scale-[0.98] ${plan.popular
                      ? 'bg-[#00809D] hover:bg-[#006d87] text-white shadow-lg shadow-[#00809D]/25 hover:shadow-xl hover:shadow-[#00809D]/35'
                      : 'bg-gray-900 hover:bg-[#00809D] text-white shadow-lg hover:shadow-xl hover:shadow-[#00809D]/25'
                      }`}
                  >
                    Get Started
                    <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-0.5 transition-transform" />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* ─── Comparison Table ─── */}
      <div className="px-4 sm:px-6 lg:px-8 pb-16 sm:pb-20 lg:pb-24">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeUp(0.2)} className="text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3">
              Compare All Features
            </h2>
            <p className="text-gray-500 text-base sm:text-lg">
              See exactly what you get with each plan
            </p>
          </motion.div>

          <motion.div {...fadeUp(0.3)} className="overflow-x-auto rounded-2xl border border-gray-200 shadow-lg">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left py-4 px-5 sm:px-6 text-sm font-semibold text-gray-600 w-2/5">Feature</th>
                  <th className="text-center py-4 px-3 sm:px-4 text-sm font-semibold text-gray-600">Growth</th>
                  <th className="text-center py-4 px-3 sm:px-4 text-sm font-semibold text-[#00809D] bg-[#00809D]/5">Velocity</th>
                  <th className="text-center py-4 px-3 sm:px-4 text-sm font-semibold text-gray-600">Surge</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[
                  { feature: "Complexity", growth: "Simple & Profitable", velocity: "Multi-channel & Scaling", surge: "Full Market Saturation" },
                  { feature: "Communication", growth: "Monthly", velocity: "Bi-weekly", surge: "Weekly / On-demand" },
                  { feature: "Ad Spend Focus", growth: "Efficiency (Low Waste)", velocity: "Volume (High Growth)", surge: "Dominance (Market Share)" },
                ].map((row, i) => (
                  <tr key={i} className="hover:bg-gray-50/50 transition-colors">
                    <td className="py-3.5 px-5 sm:px-6 text-sm text-gray-700 font-medium">{row.feature}</td>
                    {['growth', 'velocity', 'surge'].map((plan) => (
                      <td
                        key={plan}
                        className={`text-center py-3.5 px-3 sm:px-4 text-sm ${plan === 'velocity' ? 'bg-[#00809D]/[0.02]' : ''
                          }`}
                      >
                        {typeof row[plan] === 'boolean' ? (
                          row[plan] ? (
                            <div className="inline-flex w-6 h-6 rounded-full bg-[#00809D]/10 items-center justify-center">
                              <Check className="w-3.5 h-3.5 text-[#00809D]" />
                            </div>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )
                        ) : (
                          <span className={`font-medium ${plan === 'growth' ? 'text-[#00809D]' : 'text-gray-700'}`}>
                            {row[plan]}
                          </span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        </div>
      </div>

      {/* ─── FAQ Snippet ─── */}
      <div className="px-4 sm:px-6 lg:px-8 pb-16 sm:pb-20">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeUp(0.2)} className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Pricing Questions?</h2>
            <p className="text-gray-500">Quick answers to common pricing questions</p>
          </motion.div>

          <motion.div {...fadeUp(0.3)} className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
            {[
              { q: "Are there any hidden fees?", a: "No hidden fees. You pay the management fee + 15% of your ad spend. That's it." },
              { q: "Can I switch plans anytime?", a: "Yes! Upgrade or downgrade your plan at any time. Changes take effect on your next billing cycle." },
              { q: "What if I need a custom plan?", a: "Contact us for enterprise-level custom pricing tailored to your specific needs and budget." },
              { q: "Is there a setup fee?", a: "No setup fees. We handle all campaign setup, tracking, and onboarding at no extra cost." },
            ].map((faq, i) => (
              <div key={i} className="bg-gray-50 rounded-xl p-5 sm:p-6 border border-gray-100 hover:border-[#00809D]/20 hover:shadow-md transition-all duration-300">
                <div className="flex items-start gap-3">
                  <HelpCircle className="w-5 h-5 text-[#00809D] flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-bold text-gray-900 mb-1.5">{faq.q}</h4>
                    <p className="text-sm text-gray-500 leading-relaxed">{faq.a}</p>
                  </div>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* ─── Bottom CTA ─── */}
      <div className="px-4 sm:px-6 lg:px-8 pb-16 sm:pb-20 lg:pb-24">
        <div className="max-w-4xl mx-auto">
          <motion.div
            {...fadeUp(0.3)}
            className="relative bg-gradient-to-br from-[#00809D] to-[#006d87] rounded-3xl p-8 sm:p-10 lg:p-12 text-center overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />

            <div className="relative">
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3">
                Ready to Grow Your Business?
              </h3>
              <p className="text-white/80 text-base sm:text-lg mb-8 max-w-xl mx-auto">
                Contact us today and let's create advertising campaigns that drive real results.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
                <button
                  onClick={() => navigate('/contact')}
                  className="w-full sm:w-auto bg-white text-[#00809D] px-8 py-4 rounded-xl font-bold text-base hover:bg-gray-50 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 shadow-lg shadow-black/10 flex items-center justify-center gap-2"
                >
                  Contact Us
                  <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => navigate('/faqs')}
                  className="w-full sm:w-auto bg-white/15 backdrop-blur-sm text-white px-8 py-4 rounded-xl font-bold text-base border border-white/20 hover:bg-white/25 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
                >
                  View All FAQs
                </button>
              </div>
              <p className="text-white/60 text-sm mt-6 font-medium">
                Join 500+ businesses scaling with targeted advertising
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
